import { useState, useEffect, useRef, type ReactNode } from "react";
import { Loader2, Sparkles, Save, Copy, Check, Calendar, Image } from "lucide-react";

export function useCopyFeedback() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };
  return { copiedKey, copy };
}

export function PillSelector({
  options,
  value,
  onChange,
  testIdPrefix,
}: {
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testIdPrefix}-${o.toLowerCase().replace(/\s/g, "-")}`}
          onClick={() => onChange(o)}
          className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
            value === o
              ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
              : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{children}</label>;
}

export function GenerateButton({
  isPending,
  onClick,
  label = "Generate",
  testId = "button-generate",
}: {
  isPending: boolean;
  onClick: () => void;
  label?: string;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={isPending}
      className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-lg px-3 py-1.5 text-xs flex items-center justify-center gap-1.5 transition-colors"
    >
      {isPending ? (
        <>
          <Loader2 size={14} className="animate-spin" />
          Generating...
        </>
      ) : (
        <>
          <Sparkles size={14} />
          {label}
        </>
      )}
    </button>
  );
}

export function SchedulePopover({ onSchedule, isPending }: { onSchedule: (date: string, time: string) => void; isPending: boolean }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const containerRef = useRef<HTMLDivElement>(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        data-testid="button-schedule-content"
        onClick={() => setOpen(!open)}
        disabled={isPending}
        className="border border-border hover:border-blue-500/30 text-muted-foreground hover:text-blue-400 font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
      >
        {isPending ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
        Schedule
      </button>
      {open && (
        <div className="absolute top-full mt-2 left-0 bg-card/95 border border-border rounded-xl p-4 space-y-3 z-[60] w-64 shadow-xl">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-muted-foreground uppercase">Date</label>
            <input
              type="date"
              data-testid="input-schedule-date"
              value={date}
              min={today}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-muted-foreground uppercase">Time</label>
            <input
              type="time"
              data-testid="input-schedule-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-muted/50 border border-border focus:border-blue-500 outline-none rounded-lg px-3 py-2 text-sm text-foreground"
            />
          </div>
          <button
            data-testid="button-confirm-schedule"
            onClick={() => {
              if (!date) return;
              onSchedule(date, time);
              setOpen(false);
            }}
            disabled={!date}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold rounded-lg px-3 py-2 text-sm"
          >
            Confirm Schedule
          </button>
        </div>
      )}
    </div>
  );
}

export function ActionBar({
  text,
  imagePrompt,
  onSave,
  savePending,
  copyKey,
  copiedKey,
  onCopy,
  onGenerateImage,
  imagePending,
  onSchedule,
  schedulePending,
}: {
  text: string;
  imagePrompt?: string | null;
  onSave: () => void;
  savePending: boolean;
  copyKey: string;
  copiedKey: string | null;
  onCopy: () => void;
  onGenerateImage?: () => void;
  imagePending?: boolean;
  onSchedule?: (date: string, time: string) => void;
  schedulePending?: boolean;
}) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      <button
        data-testid="button-save-content"
        onClick={onSave}
        disabled={savePending}
        className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
      >
        {savePending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        Save
      </button>
      {onSchedule && (
        <SchedulePopover onSchedule={onSchedule} isPending={schedulePending || false} />
      )}
      {imagePrompt && onGenerateImage && (
        <button
          data-testid="button-generate-image"
          onClick={onGenerateImage}
          disabled={imagePending}
          className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
        >
          {imagePending ? <Loader2 size={12} className="animate-spin" /> : <Image size={12} />}
          Image
        </button>
      )}
      <button
        data-testid="button-copy-generated"
        onClick={onCopy}
        className="border border-border hover:border-border text-muted-foreground hover:text-foreground font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors"
      >
        {copiedKey === copyKey ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
        {copiedKey === copyKey ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function approvalBadgeStyle(status: string) {
  switch (status) {
    case "approved": return "bg-green-500/15 text-green-400 border-green-500/20";
    case "rejected": return "bg-red-500/15 text-red-400 border-red-500/20";
    default: return "bg-yellow-500/15 text-yellow-400 border-yellow-500/20";
  }
}

export function approvalLabel(status: string) {
  switch (status) {
    case "approved": return "Approved";
    case "rejected": return "Rejected";
    default: return "Pending";
  }
}
